/**
 * Utility for converting Firebase authentication error codes to user-friendly messages
 */

export const getAuthErrorMessage = (error: any): string => {
  if (!error || !error.code) {
    return error?.message || 'An unexpected error occurred. Please try again.';
  }

  switch (error.code) {
    // Sign In Errors
    case 'auth/user-not-found':
      return 'No account found with this email address. Please check your email or create a new account.';
    
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again or reset your password.';
    
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support for assistance.';
    
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later or reset your password.';
    
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please check your credentials and try again.';
    
    // Sign Up Errors
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please sign in instead.';
    
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters with a mix of letters and numbers.';
    
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled. Please contact support.';
    
    // Network/General Errors
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.';
    
    case 'auth/internal-error':
      return 'An internal error occurred. Please try again later.';
    
    case 'auth/quota-exceeded':
      return 'Service temporarily unavailable. Please try again later.';
    
    // Password Reset Errors (already handled in FirebaseService but adding for completeness)
    case 'auth/missing-email':
      return 'Please enter your email address.';
    
    case 'auth/invalid-action-code':
      return 'Invalid or expired reset link. Please request a new password reset.';
    
    case 'auth/expired-action-code':
      return 'Password reset link has expired. Please request a new one.';
    
    // Generic Firebase errors
    default:
      // If it's already a user-friendly message (doesn't contain Firebase codes), return as is
      if (error.message && !error.message.toLowerCase().includes('firebase') && !error.message.includes('auth/')) {
        return error.message;
      }
      
      // Generic fallback
      return 'Authentication failed. Please try again or contact support if the problem persists.';
  }
};
